import React, { useState, useEffect, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { Spin } from 'antd';
import type { AppDispatch } from '../EcommerceStore';
import { Typography, LineProgressBar, ConfirmModal } from "mobily-ui-library/lib";

import { SequenceManagerAction, setProductKey, displayNextSection, displaySection, getStructure, getCheckout, captureData, generateCheckoutOrder, createOrderId, callAuditServiceApi } from '../actions/SequenceManagerAction'
import { getProductOfferById } from '../actions/ProductOfferAction';
import { createShoppingCart, getShoppingCart, updateAndValidateShoppingCartById } from '../actions/ShoppingCartAction';

import ConfigData from '../EcommerceConfig.json';
import ArrowLeft from '../assets/ArrowLeft.svg';
import './SequenceManager.scss';
import { reGenerateToken } from '../api/api';
import ErrorModal from '../components/ErrorModal/ErrorModal';
import tokenService from '../services/TokenService'

declare global {
    interface Window {
        journeyName?: string;
    }
}

const NumberOption = React.lazy(() => import('../components/NumberOption/NumberOption'));
const ChooseNumber = React.lazy(() => import('../components/ChooseNumber/ChooseNumber'));
const DeliveryDetails = React.lazy(() => import('../components/DeliveryDetails/DeliveryDetails'));
const FTTHServiceability = React.lazy(() => import('../components/DeliveryDetails/FTTHServiceability'));
const SimOption = React.lazy(() => import('../components/SimOption/SimOption'));
const Checkout = React.lazy(() => import('../components/Checkout/Checkout'));
const TCCEligibilityCheck = React.lazy(() => import('../components/TCCEligibilityCheck/TCCEligibilityCheck'));
const EPlanCheck = React.lazy(() => import('../components/EPlanCheck/EPlanCheck'));
const UnpaidBills = React.lazy(() => import('../components/UnpaidBills/UnpaidBills'));
const SecurityDeposit = React.lazy(() => import('../components/SecurityDeposit/SecurityDeposit'));
const AppointmentDetails = React.lazy(() => import('../components/AppointmentDetails/AppointmentDetails'));
const PersonalDetails = React.lazy(() => import('../components/PersonalDetails/PersonalDetails'));
const AbsherVerification = React.lazy(() => import('../components/AbsherVerification/AbsherVerification'));
const UnifiedAbsherVerification = React.lazy(() => import('../components/UnifiedAbsherVerification/UnifiedAbsherVerification'));
const IDVerification = React.lazy(() => import('../components/IDVerification/IDVerification'));
const SuccessPage = React.lazy(() => import('../components/SuccessPage/SuccessPage'));
const ContractLength = React.lazy(() => import('../components/ContractLength/ContractLength'));
const CurrentOperator = React.lazy(() => import('../components/CurrentOperator/CurrentOperator'));
const CurrentNumber = React.lazy(() => import('../components/CurrentNumber/CurrentNumber'));
const FamilyPlanCheck = React.lazy(() => import('../components/FamilyPlanCheck/FamilyPlanCheck'));
const FTTHFlowRuleDetails = React.lazy(() => import('../components/FTTHFlowRuleDetails/FTTHFlowRuleDetails'));
const ODBDetails = React.lazy(() => import('../components/ODBDetails/ODBDetails'));
const EnterSIMIDDetails = React.lazy(() => import('../components/EnterSIMIDDetails/EnterSIMIDDetails'));
const MobilyOTPLine = React.lazy(() => import('../components/MobilyOTPLine/MobilyOTPLine'));
const OTPVerification = React.lazy(() => import('../components/OTPVerification/OTPVerification'));
const MNPOTPVerification = React.lazy(() => import('../components/MNPOTPVerification/MNPOTPVerification'));
const SIMSwapOTPVerification = React.lazy(() => import('../components/SIMSwapOTPVerification/SIMSwapOTPVerification'));
const DeliveryAppointment = React.lazy(() => import('../components/DeliveryAppointment/DeliveryAppointment'));

const win: any = window;
var paymentPage = win["paymentPageURL"];
var ecommercePage = win["ecommercePageURL"];
let xCorrelationId = win["x_cor_id"];

const homePage = win["home_page_url"];
const serverURL = win["base_portal_url"];
const errorPage = win["errorPageURL"];
const retryCount = win["AllowedAPIRetryCount"];

win.dataLayer = win.dataLayer || [];

function SequenceManager() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState(true);
    const [checkoutRequired, setCheckoutRequired] = useState(false);
    const [useQueryParams, setUseQueryParams] = useState(false);
    const [componentToDisplay, setComponentToDisplay] = useState(<></>);
    const [currentSection, setCurrentSection] = useState('');
    const [browserBackClick, setBrowserBackClick] = useState(false)
    const [errorStatusModal, setErrorStatusModal] = useState(false)
    const [modalTitle, setModalTitle] = useState("");
    const [modalSubtitle, setModalSubtitle] = useState("");
    const [modalSubmitText, setModalSubmitText] = useState("");
    const [modalCancelText, setModalCancelText] = useState("");
    const [tokenTracker, setTokenTracker] = useState(win['clientToken']);
    let [tokenCounter, setTokenCounter] = useState(1);
    const [cartItemDetails, setCartItemDetails] = useState({ value: 0, price: 0, quantity: 0, tax: 0 });
    const [isServiceBusyErrorVisible, setIsServiceBusyErrorVisible] = useState(false);

    const SequenceManager = useSelector((state: any) => state.SequenceManager);
    const ShoppingCart = useSelector((state: any) => state.ShoppingCart);
    const FamilyPlanCheckData = useSelector((state: any) => state.FamilyPlanCheck);
    const FTTHFlowRuleDetailsData = useSelector((state: any) => state.FTTHFlowRuleDetails);
    const ODBDetailsData = useSelector((state: any) => state.ODBDetails);
    const EPlanCheckData = useSelector((state: any) => state.EPlanCheck);
    const CheckoutData = useSelector((state: any) => state.paymentMethodAndTermsConditions);
    const TCCEligibilityCheckData = useSelector((state: any) => state.TCCEligibilityCheck);
    const CurrentNumberState = useSelector((state: any) => state.CurrentNumber);

    const appState = SequenceManager.appState;
    const sectionsVisited = SequenceManager.sectionsVisited;
    const sectionToShow = SequenceManager.sectionToShow;
    const orderStatus = SequenceManager.orderStatus;
    const { rtl, generalErrModal, generalErrRes } = SequenceManager;

    const urlParams = new URLSearchParams(window.location.search);
    let productURL = sessionStorage.getItem("productURL");
    let refId = CheckoutData.requestBody.steps[0].fields[0].value;
    const paymentMethod = CheckoutData.requestBody.steps[0].options[0].value
    const otpType = TCCEligibilityCheckData.requestBody.steps[0].fields[5].value;
    const phoneNumber: any = CurrentNumberState?.requestBody?.steps?.[0]?.fields?.[0]?.value;
    const simSwapPhoneNumber = phoneNumber ? phoneNumber.replace(/^0/, '966') : '';

    console.log('🚀-- SequenceManager -- ' + appState)
    console.log("simSWAP:");
    const shoppingCart = ShoppingCart.shoppingCart;
    const homePageUrl = win["home_page_url"];

    useEffect(() => {
        const offerContainer = shoppingCart?.offerContainer;
         if (shoppingCart?.status === 504 || shoppingCart?.status === 500) {
                setLoading(false);
                setIsServiceBusyErrorVisible(true);
                return;
            }
        if (offerContainer && offerContainer.length > 0 && offerContainer[0]?.cartItem?.length > 0) {
            const oneTimeSetupFeeCartItem = offerContainer?.[0]?.cartItem?.find(
                (cartItem: any) => cartItem?.product?.name === SequenceManager?.selectedProduct
            );
            const oneSetupFee = oneTimeSetupFeeCartItem ? oneTimeSetupFeeCartItem?.itemTotalPrice?.filter((price: any) => (price?.priceType === 'Onetime'))[0]?.price?.dutyFreeAlteredAmount?.value : 0;
            const data = {
                price: oneSetupFee,
                value: oneTimeSetupFeeCartItem?.itemPrice?.[0]?.price?.taxIncludedAmount?.value,
                quantity: 1,
                tax: oneTimeSetupFeeCartItem?.itemPrice?.[0]?.price?.taxAmount
            };
            setCartItemDetails({ ...cartItemDetails, ...data });
        }
    }, [shoppingCart, SequenceManager]);


    useEffect(() => {
        if ((!tokenTracker || tokenTracker === 'null') && tokenCounter < retryCount) {
            reGenerateToken()
                .then((res: any) => res.text())
                .then((data: any) => {
                    setTokenTracker(data)
                    win["clientToken"] = data;
                    setTokenCounter(++tokenCounter)
                })
        } else {
            if (tokenCounter === retryCount) {
                window.location.href = serverURL + errorPage + `?errorSource=${rtl ? "التجارة الإلكترونية" : "ecommerce"}`;
            }
        }
    }, [tokenTracker, tokenCounter])

    useEffect(() => {
        stopBrowserBack()
        console.log('----------- ON MOUNT ----------------');
        let status = urlParams.get('status');
        let edata = urlParams.get('edata');

        if (tokenTracker && tokenTracker !== 'null') {
            if (status || edata) {
                setLoading(true);
                // let decode = window.atob(edata ? edata : "");
                let decode = window.decodeURIComponent(atob(edata ? edata : ""));
                let params = JSON.parse(decode);
                let spcartId = params?.scId;
                let journeyName = params?.jN;
                let xCorrelationId = params?.xCoId;
                win.auditJourneyName = journeyName;
                win.auditScId = spcartId;
                win.auditXCorrelationId = xCorrelationId;

                const req = { "dataT": `getdbtkn~${xCorrelationId}~${spcartId}` };
                tokenService(req).then(res => {
                    if (res) {
                        win["clientToken"] = res;
                        setLoading(false);
                    }
                })

                setDocumentTitle(params.j);
                if (status === 'failure') {
                    // win.auditStatusCode = statusCode;
                    // //Audit API
                    // const saudiTime = getRiyadhISOString();
                    // const err = {
                    //     config: {
                    //         url: 'After Payment failed at ecommerce',
                    //         headers: { value: "Received the Payment status as failure", 'X-Timestamp': saudiTime },
                    //         method: '',
                    //         data: JSON.stringify(`payment got failed and went to error page in ecommerce`)
                    //     }
                    // };
                    // dispatch({ type: SequenceManagerAction.GENERIC_ERROR, payload: err });

                    window.location.href = serverURL + errorPage + `?errorSource=${rtl ? "التجارة الإلكترونية" : "ecommerce"}`;
                } else {
                    if (edata) {
                        setUseQueryParams(true);
                        setCheckoutRequired(true);
                        // dispatch(getProductOfferById("prepaid_150-of_h7uiw", SequenceManager.lang));
                        if (status === 'success' && (journeyName === 'KEEP_NUMBER_eSIM' || journeyName === 'PreSSA')) {
                            dispatch(generateCheckoutOrder(spcartId, journeyName, simSwapPhoneNumber));
                        } else {
                            setTimeout(() => {
                                dispatch(getShoppingCart(spcartId))
                            }, 1000);
                        }
                    }
                }
            } else {
                dispatch({ type: SequenceManagerAction.INIT })
            }
        }
    }, [tokenTracker]);

    useEffect(() => {
        switch (appState) {
            case SequenceManagerAction.INIT:
                setLoading(true);
                dispatch(setProductKey());
                break;

            case SequenceManagerAction.SET_PRODUCT_KEY_COMPLETE:
                if (SequenceManager?.productKey && SequenceManager?.journey && SequenceManager?.category) {
                    setDocumentTitle(SequenceManager.journey);
                    dispatch(getProductOfferById(SequenceManager.productKey));
                } else {
                    setLoading(false);
                    const errorTile = t('something_is_wrong');
                    const errorDec = t('try_again_later');
                    dispatch({
                        type: SequenceManagerAction.GENERAL_ERROR_MODAL,
                        payload: {
                            generalErrModal: true,
                            generalErrRes: {
                                errorTile,
                                errorDec
                            }
                        }
                    });
                }

                break;

            case SequenceManagerAction.LOAD_PRODUCT_OFFER_COMPLETE:
                dispatch(createShoppingCart());
                break;

            case SequenceManagerAction.CREATE_SHOPPING_CART_COMPLETE:
                if (SequenceManager.journey.includes("FTTH")) {
                    dispatch(createOrderId());
                } else {
                    dispatch(updateAndValidateShoppingCartById(ShoppingCart.shoppingCart.id, true));
                }
                break;

            case SequenceManagerAction.CREATE_ORDER_COMPLETE:
                dispatch(updateAndValidateShoppingCartById(ShoppingCart.shoppingCart.id, true));
                break;

            case SequenceManagerAction.SHOPPING_CART_VALIDATION_COMPLETE:
            case SequenceManagerAction.UPDATE_VALIDATE_CART_COMPLETE:
            case SequenceManagerAction.CAPTURE_DATA_COMPLETE:
            case SequenceManagerAction.GET_CHECKOUT_COMPLETE:
                setLoading(true);
                setCheckoutRequired(false);
                dispatch(getStructure());
                break;

            case SequenceManagerAction.STRUCTURE_UPDATE_COMPLETE:
                if (useQueryParams) {
                    setUseQueryParams(false);
                    let status = urlParams.get('status');
                    let edata = urlParams.get('edata');
                    if (edata) {
                        // let decode = window.atob(edata?edata:"");
                        let decode = window.decodeURIComponent(atob(edata ? edata : ""));
                        let params = JSON.parse(decode);
                        let decodedParams = {
                            orderId: params.oId,
                            productURL: params.pURL,
                            journey: params.j,
                            selectedSimType: params.sST,
                            selectedProduct: params.sP,
                            selectedProductLabel: params.sPL,
                            selectedTier: params.sT,
                            planType: params.pT,
                            productType: params.prT,
                            productExternalId: params.pE,
                            selectedMsisdn: params.sN,
                            selectedMsisdnType: params.sNT,
                            operator: params.o,
                            paymentMethod: params.pM,
                            payType: params.pTyp,
                            nationCode: params.nCode,
                            email: params.email,
                            maskedEmail: params.maEml,
                            xCorrelationId: params.xCoId,
                            journeyName: params.jN,
                            step: params.step
                        }

                        if (status) {
                            if (status === 'success') {
                                dispatch(displaySection('SuccessPage', decodedParams));
                            }
                            if (status === 'failure') {
                                dispatch(displaySection('paymentMethodAndTermsConditions', decodedParams));
                            }
                        }
                        if (decodedParams.step === "avtr" || decodedParams.step === "uavtr") {
                            let sectionToNavigate = ConfigData.section_mappings.filter((section: any) => section.step == decodedParams.step);
                            let tccToken: any = "";
                            let nafathStatus: any = "";
                            let source = urlParams.get('source');
                            if (source) {
                                let nafathVersion = urlParams.get('nafathVersion');
                                nafathStatus = urlParams.get('nafathStatus');
                                if (nafathVersion === "versionOne") {
                                    tccToken = urlParams.get('IAMToken') ? urlParams.get('IAMToken') : "NO_TOKEN";
                                } else {
                                    tccToken = nafathVersion;
                                }
                            } else {
                                tccToken = "";
                            }
                            decodedParams = Object.assign(decodedParams, { tccToken: tccToken, nafathStatus: nafathStatus });
                            dispatch(displaySection(sectionToNavigate[0].section, decodedParams));
                        }
                        // window.history.pushState("", "", "/");
                    }
                } else {
                    if (currentSection === 'FamilyPlanCheck' && FamilyPlanCheckData.redirectUrl) {
                        if (FamilyPlanCheckData?.familyPlanCheckResponse?.data?.ErrorCode === 9999) {
                            //Audit API
                            const err = FamilyPlanCheckData?.familyPlanCheckResponse;
                            dispatch({ type: SequenceManagerAction.GENERIC_ERROR, payload: err });
                        } else {
                            window.open(FamilyPlanCheckData.redirectUrl, "_self");
                        }
                    } else if (currentSection === 'FTTHFlowRuleDetails' && FTTHFlowRuleDetailsData.requestBody.steps[0].fields[0].value === "false") {
                        window.open(SequenceManager.productURL + '?viewPlanDetails', '_self')?.focus();
                    } else if (currentSection === 'ODBDetails' && !ODBDetailsData.requestBody.steps[0].fields[3].value) {
                        window.open(SequenceManager.productURL + '?viewPlanDetails', '_self')?.focus();
                    } else if (currentSection === 'paymentMethodAndTermsConditions') {
                        if (SequenceManager.journeyName === 'KEEP_NUMBER_eSIM' || SequenceManager.journeyName === 'Cold_SSA' || SequenceManager.journeyName === 'PreSSA' || SequenceManager.journeyName === 'NEW_NUMBER_eSIM') {
                            let data: any = {
                                scId: ShoppingCart.shoppingCart.id,
                                j: SequenceManager.journey,
                                sST: SequenceManager.selectedSimType,
                                sP: SequenceManager.selectedProduct,
                                sPL: SequenceManager.selectedProductLabel,
                                pT: SequenceManager.planType,
                                prT: SequenceManager.productType,
                                o: SequenceManager.operator,
                                xCoId: SequenceManager.xCorrelationId,
                                jN: SequenceManager.journeyName,
                                pURL: SequenceManager.productURL,
                                step: SequenceManager.journeyName === 'Cold_SSA' || SequenceManager.journeyName === "NEW_NUMBER_eSIM" ? "uavtr" : "",
                            }
                            let encoded = window.btoa(encodeURIComponent(JSON.stringify(data)));
                            // let encoded = window.btoa(JSON.stringify(data));
                            let returnUrl = ecommercePage + '?edata=' + encoded + '&xCoId=' + SequenceManager.xCorrelationId;
                            let totalPay = ShoppingCart.shoppingCart.cartTotalPrice.filter((price: any) => (price.priceType === 'Onetime'))[0].price.taxIncludedAlteredAmount.value;
                            if (totalPay > 0) {
                                if (SequenceManager.journeyName === 'KEEP_NUMBER_eSIM') {
                                    window.location.href = win["dpcPaymentUrl"] + '?returnUrl=' + returnUrl + '&refId=' + refId + '&ptype=autr' + '&jn=' + SequenceManager.journeyName;
                                } else {
                                    window.location.href = win["dpcPaymentUrl"] + '?returnUrl=' + returnUrl + '&refId=' + refId + '&ptype=autr' + '&src=' + SequenceManager.journeyName + '&jn=' + SequenceManager.journeyName;
                                }
                            } else {
                                if (SequenceManager.journeyName === 'KEEP_NUMBER_eSIM') {
                                    dispatch(generateCheckoutOrder(ShoppingCart.shoppingCart.id, SequenceManager.journeyName, simSwapPhoneNumber));
                                } else if (SequenceManager.journeyName === 'Cold_SSA' || SequenceManager.journeyName === "NEW_NUMBER_eSIM") {
                                    dispatch(displaySection("UnifiedAbsherVerification"))
                                } else {
                                    dispatch(generateCheckoutOrder(ShoppingCart.shoppingCart.id, SequenceManager.journeyName, simSwapPhoneNumber));
                                }
                            }
                        } else {
                            dispatch(generateCheckoutOrder(ShoppingCart.shoppingCart.id, SequenceManager.journeyName, simSwapPhoneNumber));
                        }
                    } else if (currentSection === 'EPlanCheck' && EPlanCheckData.requestBody.steps[0].options[0].value) {
                        window.open(win["home_page_url"], "_self")?.focus();
                    } else if (currentSection === 'UnifiedAbsherVerification') {
                        dispatch(generateCheckoutOrder(ShoppingCart.shoppingCart.id, SequenceManager.journeyName, simSwapPhoneNumber));

                    } else {
                        dispatch(displayNextSection());
                    }
                }
                break;

            case SequenceManagerAction.SECTION_COMPLETE:
                setLoading(true);
                dispatch(captureData());
                break;

            case SequenceManagerAction.SHOPPING_CART_UPDATE_COMPLETE:
                if (checkoutRequired) {
                    setLoading(true);
                    dispatch(getCheckout());
                }
                break;

            case SequenceManagerAction.GENERIC_ERROR:
                if (SequenceManager?.error?.response?.status === 401 || SequenceManager?.error?.status === 401) {
                    setLoading(false);
                    setErrorStatusModal(true);
                } else {
                    dispatch(callAuditServiceApi(SequenceManager, ShoppingCart.shoppingCart.id, currentSection));
                }
                break;

            case SequenceManagerAction.DISPLAY_NEXT_SECTION:

            case SequenceManagerAction.DISPLAY_PREVIOUS_SECTION:
                let section = SequenceManager.structure.sections[SequenceManager.sectionsVisited.length];
                // let section = SequenceManager.structure.sections[5];
                let sectionDisplay = ConfigData.section_mappings.filter((sect: any) => {
                    return (sect.checkoutSectionId === section.sectionId)
                })[0].section;
                displaySectionUI(sectionDisplay);
                break;

            case SequenceManagerAction.DISPLAY_SECTION:
                displaySectionUI(sectionToShow);
                break;
        }
    }, [appState, sectionsVisited])

    useEffect(() => {
        if (orderStatus) {
            let status = urlParams.get('status');
            let edata = urlParams.get('edata');
            let decode = window.decodeURIComponent(atob(edata ? edata : ""));
            let params = edata ? JSON.parse(decode) : "";
            let spcartId = edata ? params.scId : ShoppingCart.shoppingCart.id;
            let journeyName = edata ? params.jN : SequenceManager.journeyName;
            if (status === 'success' && (journeyName === 'KEEP_NUMBER_eSIM' || journeyName === 'PreSSA')) {
                dispatch(getShoppingCart(spcartId));
            } else if (orderStatus.statusCode === "MBE_000" || orderStatus.statusCode === "0000") {
                const totalPay = ShoppingCart.shoppingCart.cartTotalPrice.filter((price: any) => (price.priceType === 'Onetime'))[0].price.taxIncludedAlteredAmount.value;
                // naviagte to success page when total pay is 0 for Cold SSA. (nothing to pay)
                if (paymentMethod === 'Cash on Delivery' || totalPay === 0) {
                    // window.history.pushState("", "", "/");
                    dispatch(displaySection('SuccessPage', { orderId: SequenceManager.orderId }));
                } else if (paymentMethod === 'Upfront Payment') {
                    let data: any = {
                        scId: ShoppingCart.shoppingCart.id,
                        oId: SequenceManager.orderId,
                        pURL: SequenceManager.productURL,
                        j: SequenceManager.journey,
                        sST: SequenceManager.selectedSimType,
                        sP: SequenceManager.selectedProduct,
                        sPL: SequenceManager.selectedProductLabel,
                        pT: SequenceManager.planType,
                        prT: SequenceManager.productType,
                        pE: SequenceManager.productExternalId,
                        sN: SequenceManager.selectedMsisdn,
                        sNT: SequenceManager.selectedMsisdnType,
                        sT: SequenceManager.selectedTier,
                        o: SequenceManager.operator,
                        pTyp: SequenceManager.payType,
                        nCode: SequenceManager.nationCode,
                        email: SequenceManager.email,
                        maEml: SequenceManager.maskedEmail,
                        xCoId: SequenceManager.xCorrelationId,
                        jN: SequenceManager.journeyName,
                        step: ""
                    }
                    let encoded = window.btoa(encodeURIComponent(JSON.stringify(data)));
                    // let encoded = window.btoa(JSON.stringify(data));
                    let paymentUrl = paymentPage + '?oId=' + SequenceManager.orderId + '&pId=' + SequenceManager.paymentTransactionId;
                    let returnUrl = ecommercePage + '?edata=' + encoded + '&xCoId=' + SequenceManager.xCorrelationId;
                    if (SequenceManager.journeyName === 'KEEP_NUMBER_eSIM' || SequenceManager.journeyName === 'Cold_SSA' || SequenceManager.journeyName === 'PreSSA' || SequenceManager.journeyName === "NEW_NUMBER_eSIM") {
                        dispatch(displaySection('SuccessPage', { orderId: SequenceManager.orderId }));
                    } else {
                        window.location.href = paymentUrl + '&returnUrl=' + returnUrl;
                    }
                }
            } else {
                //Audit API
                const err = {
                    config: {
                        url: 'postProductOrder',
                        headers: { value: "From postProductOrder got wrong statusCode" },
                        method: '',
                        data: JSON.stringify(`Recived Wrong statusCode from postProductOrder Response`)
                    }
                };
                dispatch({ type: SequenceManagerAction.GENERIC_ERROR, payload: err });
            }
        }
    }, [orderStatus])

    const handleStopTracking = () => {
        setBrowserBackClick(false)
        setLoading(true);
        if (SequenceManager.journey === "NEW_SIM" || SequenceManager.journey === "ESIM") {
            win.dataLayer.push({
                event: 'purchase_exit',
                event_name: 'purchase_exit',
                ecommerce: {
                    exit_step: sectionsVisited.length + 1,
                    page_url: `${ecommercePage}#${currentSection.replace(/([A-Z])/g, "-$1").toLowerCase().replace(/^-/, '')}`,
                    exit_page: currentSection.replace(/([A-Z])/g, " $1").trim(),
                }
            })
        }
        win.onpopstate = undefined
        if (productURL) {
            window.location.href = productURL;
        } else {
            window.location.href = homePage
        }
    }

    const stopBrowserBack = () => {
        win.history.pushState(null, '', win.location.href)
        win.onpopstate = () => {
            win.history.pushState(null, '', win.location.href)
            browserBackModalContent();
        }
    }

    const browserBackModalContent = () => {
        if (SequenceManager.journey === "COLD_SSA") {
            setModalTitle(t("stop_sim_activation"));
            setModalSubtitle(t("sure_want_to_stop_activating"));
            setModalSubmitText(t("continue_activation"));
            setModalCancelText(t("stop_activation"));
            setBrowserBackClick(true);
        } else {
            setModalTitle(t("stop_purchase_with_qus"));
            setModalSubtitle(t("you_are_about_to_stop_purchase"));
            setModalSubmitText(t("continue_purchase"));
            setModalCancelText(t("stop_purchase_upper_case"));
            setBrowserBackClick(true);
        }
    }

    const setDocumentTitle = (journey: any) => {
        let title = "";
        if (journey === "COLD_SSA") {
            title = t("sim_self_activation");
        }
        document.title = title;
    }

    const onBackPressed = (sectionId: any) => {
        // let sectionIndex = SequenceManager.structure.sections.findIndex((section: any) => (currentSection === section.sectionId));
        // let prevSection = SequenceManager.structure.sections[sectionIndex - 1];
        // if (sectionIndex === 0) {
        //     window.location.href = SequenceManager.productURL;
        // } else {
        //     let step = 1;
        //     setLoading(true);
        //     if (prevSection.sectionId.includes('E50Check')) {
        //         step = 2;
        //     }
        //     dispatch(displayPrevSection(step));
        // }

        if (sectionId === "OTPVerification" && otpType.toUpperCase() === 'MOBILY') {
            dispatch(displaySection('MobilyOTPLine'));
        } else {
            if (SequenceManager.journey === "COLD_SSA") {
                setModalTitle(t("stop_sim_activation"));
                setModalSubtitle(t("sure_want_to_stop_activating"));
                setModalSubmitText(t("continue_activation"));
                setModalCancelText(t("stop_activation"));
                setBrowserBackClick(true);
            } else {
                setModalTitle(t("stop_purchase_with_qus"));
                setModalSubtitle(t("you_are_about_to_stop_purchase"));
                setModalSubmitText(t("continue_purchase"));
                setModalCancelText(t("stop_purchase_upper_case"));
                setBrowserBackClick(true);
            }
        }

    }

    const displaySectionUI = (sectionId: any) => {
        setLoading(false);
        setCurrentSection(sectionId);
        const name = SequenceManager.journeyName ?? "";
        window.journeyName = name;
        window.dispatchEvent(
            new CustomEvent("journeyName-ready", { detail: name })
        );
        win.dataLayer.length = 0;
        if (SequenceManager.journey === "NEW_SIM" || SequenceManager.journey === "ESIM") {
            if (sectionsVisited.length === 0) {
                win.dataLayer.push({
                    event: 'begin_checkout',
                    event_name: 'begin_checkout',
                    ecommerce: {
                        currency: 'SAR',
                        language_selected: rtl ? "AR" : "EN",
                        login_type: 'not_logged_in',
                        value: cartItemDetails?.value,
                        items: [{
                            item_id: SequenceManager.selectedProductLabel.split(" ").join("_").toLowerCase(),
                            item_name: SequenceManager.selectedProductLabel,
                            item_brand: 'Mobily',
                            item_category: SequenceManager.productType === 'D' ? 'data' : 'voice_data',
                            item_category2: '',
                            item_category3: '',
                            item_type: SequenceManager.planType,
                            item_variant: '',
                            price: cartItemDetails?.price,
                            currency: 'SAR',
                            quantity: 1,
                            tax: cartItemDetails?.tax
                        }]
                    }
                });
            }
            win.dataLayer.push({ ecommerce: null });
            win.dataLayer.push({
                event: 'virtual_page_view',
                event_name: 'virtual_page_view',
                ecommerce: {
                    virtual_page_view_name: sectionId.replace(/([A-Z])/g, " $1").trim(),
                    virtual_page_view_product_name: SequenceManager.selectedProduct,
                    page_url: `${ecommercePage}#${sectionId.replace(/([A-Z])/g, "-$1").toLowerCase().replace(/^-/, '')}`,
                    step_number: sectionsVisited.length + 1
                }
            })
        }
        let journeyTitle: any = "";
        if (SequenceManager.journey === "NEW_SIM" || SequenceManager.journey === "ESIM" || SequenceManager.journey.includes("FTTH")) {
            journeyTitle = (
                <>
                    {rtl ? <p style={{ display: 'flex' }}><span style={{ direction: "ltr", margin: '0 5px' }}> {SequenceManager.selectedProductLabel}</span></p> : <span>{SequenceManager.selectedProductLabel}</span>}
                </>
            )

        } else if (SequenceManager.journey === "MNP") {
            journeyTitle = (
                <>
                    {rtl ? <p style={{ display: 'flex' }}><span>{t("transfer_to")}</span> <span style={{ direction: "ltr", margin: '0 5px' }}> {SequenceManager.selectedProductLabel}</span></p> : <span>{t("transfer_to") + SequenceManager.selectedProductLabel}</span>}
                </>
            )
        } else if (SequenceManager.journey === "COLD_SSA") {
            journeyTitle = "";
        } else {
            journeyTitle = rtl ? <span style={{ direction: "ltr", display: 'block' }}> {SequenceManager.selectedProductLabel}</span> : SequenceManager.selectedProductLabel;
        }
        setComponentToDisplay(<>
            <div id="ecommerceJourneyName" style={{ display: 'none' }}>{SequenceManager.journeyName}</div>
            <div id="xCorrelationId" style={{ display: 'none' }}>{xCorrelationId}</div>
            <div className='sectionId' id="sectionId" style={{ display: 'none' }}>{sectionId}</div>

            {sectionId === 'SuccessPage' ? <></> : (<div className='productOfferName-container' >
                <Typography rtl={rtl} className='productOfferName' >{journeyTitle}</Typography>
                <LineProgressBar rtl={rtl} percent={getCompletionPercent()}></LineProgressBar>
                <div className='sequenceManagerBack'>
                    <img className="backButton_arrow" src={ArrowLeft} alt='arrow' onClick={() => { onBackPressed(sectionId) }} />
                    {/* <Typography className='sequenceManagerBackText'>
                            {t("backTo").toUpperCase()} {previousSection}
                        </Typography> */}
                </div>
            </div>)}

            {getComponentById(sectionId)}
        </>);
        window.scroll({ top: 0, left: 0 });
    }

    const getComponentById = (sectionId: String) => {
        return (
            <>
                {sectionId === 'NumberOption' && <NumberOption />}
                {sectionId === 'ChooseNumber' && <ChooseNumber />}
                {sectionId === 'SimOption' && <SimOption />}
                {sectionId === 'TCCEligibilityCheck' && <TCCEligibilityCheck />}
                {sectionId === 'EPlanCheck' && <EPlanCheck />}
                {sectionId === 'UnpaidBills' && <UnpaidBills />}
                {sectionId === 'SecurityDeposit' && <SecurityDeposit />}
                {sectionId === 'AppointmentDetails' && <AppointmentDetails />}
                {sectionId === 'PersonalDetails' && <PersonalDetails />}
                {sectionId === 'IDVerification' && <IDVerification />}
                {sectionId === 'AbsherVerification' && <AbsherVerification />}
                {sectionId === 'UnifiedAbsherVerification' && <UnifiedAbsherVerification />}
                {sectionId === 'DeliveryDetails' && <DeliveryDetails />}
                {sectionId === 'FTTHServiceability' && <FTTHServiceability />}
                {sectionId === 'paymentMethodAndTermsConditions' && <Checkout />}
                {sectionId === 'SuccessPage' && <SuccessPage />}
                {sectionId === 'ContractLength' && <ContractLength />}
                {sectionId === 'CurrentOperator' && <CurrentOperator />}
                {sectionId === 'CurrentNumber' && <CurrentNumber />}
                {sectionId === 'FamilyPlanCheck' && <FamilyPlanCheck />}
                {sectionId === 'FTTHFlowRuleDetails' && <FTTHFlowRuleDetails />}
                {sectionId === 'ODBDetails' && <ODBDetails />}
                {sectionId === 'EnterSIMIDDetails' && <EnterSIMIDDetails />}
                {sectionId === 'MobilyOTPLine' && <MobilyOTPLine />}
                {sectionId === 'OTPVerification' && <OTPVerification />}
                {sectionId === 'MNPOTPVerification' && <MNPOTPVerification />}
                {sectionId === 'SIMSwapOTPVerification' && <SIMSwapOTPVerification />}
                {sectionId === 'DeliveryAppointment' && <DeliveryAppointment />}
            </>
        );
    }

    const getCompletionPercent = () => {
        let structure = SequenceManager.structure.sections;
        let percentComplete = 0;
        if (structure) {
            let containE50Section = structure.filter((section: any) => { return section.sectionId.includes('E50Check') }).length > 0;
            let numOfSectionsVisited = SequenceManager.sectionsVisited.length;
            let numOfDisplaySections = containE50Section ? (structure.length - 1) : structure.length;
            percentComplete = ((numOfSectionsVisited + 1) / numOfDisplaySections) * 100;
        }
        return percentComplete;
    }

    const handleErrorModal = () => {
        //redirect to Ecommerce Product URL
        setLoading(true);
        setErrorStatusModal(false);
        document.body.style.overflow = 'auto';
        if (generalErrRes?.sourceCode) {
            if (generalErrRes?.sourceCode === 'MAPI-260-3') {
                window.location.href = win['home_page_url']
            } else {
                window.location.reload();
            }
        } else if (SequenceManager?.productURL) {
            window.location.href = SequenceManager?.productURL;
        } else {
            window.location.href = win['home_page_url']
        }
    }

    if (loading) {
        return <Spin className="spinLoader" size="large" />
    } else if (errorStatusModal) {
        return (
            <ErrorModal
                id={'errormodal-statuscodes'}
                rtl={false}
                title={t('authentication_try_again')} // need to update
                description={''}
                firstButtonText={t('try_absher_again')}
                modalFirstButtonClick={handleErrorModal}
            />
        )
    } else {
        return (
            <Suspense fallback={<Spin className="spinLoader" size="large" />}>
                <div className="sequenceManager">
                    {componentToDisplay}
                    <div className='browserBackClickModal'>
                        <ConfirmModal
                            id='browserBackClickModal'
                            idBtn='browserBackClickModalSubmit'
                            idBtnOutLine='browserBackClickModalOutlineSubmit'
                            idCloseIcon='browserBackClickModalClose'
                            title={modalTitle}
                            subText={modalSubtitle}
                            buttonName={modalSubmitText}
                            outlineButtonName={modalCancelText}
                            showModal={browserBackClick}
                            onClickClose={() => setBrowserBackClick(false)}
                            onclickButton={() => setBrowserBackClick(false)}
                            onclickOutLineButton={handleStopTracking}
                        />
                    </div>
                </div>
                {
                    generalErrModal && (
                        <ErrorModal
                            id={'errormodal-statuscodes'}
                            rtl={false}
                            title={generalErrRes?.errorTile} // need to update
                            description={generalErrRes?.errorDec}
                            firstButtonText={generalErrRes?.sourceCode === 'MAPI-260-3' ? t('got_it') : t('try_absher_again')}
                            modalFirstButtonClick={handleErrorModal}
                        />
                    )
                }
                {isServiceBusyErrorVisible && (
                    <ErrorModal
                        id="serviceBusyErrorModal"
                        className="Error__Modal_updated"
                        rtl={rtl}
                        title={t("service_busy_message")}
                        description={""}
                        firstButtonText={t("got_it")}
                        modalFirstButtonClick={() => {
                            setIsServiceBusyErrorVisible(false);
                            document.body.style.overflow = 'auto';
                            window.location.href = homePageUrl;
                        }}
                    />
                )}
            </Suspense>
        )
    }
}

export default SequenceManager;